'use client'

import AppStoreIcon from '@/assets/icons/app-store.svg'
import ArrowRightRounded from '@/assets/icons/arrow-right.svg'
import PlayStoreIcon from '@/assets/icons/play-store.svg'
import { useClickOutside } from '@/core/hooks/use-click-outside'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

type Props = {
  appStoreUrl?: string
  playStoreUrl?: string
}

export const DownloadLinks: React.FC<Props> = ({
  appStoreUrl,
  playStoreUrl,
}) => {
  const t = useTranslations()
  const [expanded, setExpanded] = useState(false)
  const linksRef = useClickOutside<HTMLDivElement>(() => setExpanded(false))
  const toggleButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false)
    }
    if (expanded) {
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [expanded])

  const renderDownloadOptions = () => (
    <div
      className='animate-fade-down animate-duration-200 absolute top-full right-0 left-0 z-10 mt-2 flex flex-col gap-2 rounded-lg bg-white p-2 shadow-lg'
      role='menu'
      aria-label={t('becomePartner.downloadDriver')}
    >
      <a
        href={appStoreUrl}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => setExpanded(false)}
        className='text-sky-blue hover:bg-lighterBlue flex items-center justify-center gap-2 rounded-lg px-4 py-2 transition-colors'
        role='menuitem'
      >
        <AppStoreIcon
          width={30}
          height={30}
          className='fill-sky-blue text-sky-blue'
          aria-hidden='true'
        />
        <span>{t('becomePartner.appStore')}</span>
      </a>
      <a
        href={playStoreUrl}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => setExpanded(false)}
        className='text-sky-blue hover:bg-lighterBlue flex items-center justify-center gap-2 rounded-lg px-4 py-2 transition-colors'
        role='menuitem'
      >
        <PlayStoreIcon
          width={30}
          height={30}
          className='fill-sky-blue text-sky-blue'
          aria-hidden='true'
        />
        <span>{t('becomePartner.playStore')}</span>
      </a>
    </div>
  )

  return (
    <div className='relative' ref={linksRef}>
      <button
        ref={toggleButtonRef}
        onClick={() => setExpanded(prev => !prev)}
        className='text-sky-blue xss:items-center xss:gap-2 relative flex cursor-pointer gap-1 rounded text-center text-lg font-medium'
        aria-expanded={expanded}
        aria-haspopup='menu'
        aria-controls='download-menu'
        id='download-toggle'
      >
        <ArrowRightRounded
          className='xss:mt-0 mt-1 rotate-90'
          aria-hidden='true'
        />
        <span>{t('becomePartner.downloadDriver')}</span>
      </button>

      {expanded ? (
        <div id='download-menu' role='presentation'>
          {renderDownloadOptions()}
        </div>
      ) : null}
    </div>
  )
}
